// src/encounter/invoice-management-discount-dialog.tsx
// Invoice-wide MANAGEMENT DISCOUNT — Flutter `add_management_discount_popup.dart`, opened from
// the totals panel (invoice_services_widget.dart:852-890: the Management Discount row when one
// is set, otherwise the Patient Payable row, and only while the invoice is not ISSUED).
//
// SELF-PAY ONLY. Flutter gates both the totals row and the tap on
// `sponsorType == paymentTypeSelfPay` — a sponsored invoice's balance is settled with the payer,
// not discounted at the desk. The host passes `disabled` for the non-self-pay case.
//
// Wire: `POST /Billing/ManagementDiscount/Create { invoiceId, type, amount, reason }` where type
// is 'Amt' (flat) or 'Pct' (percentage) — the shipped `useAddManagementDiscount`, whose own doc
// records the Flutter request model. The backend allocates across eligible services and the hook
// invalidates the invoice, so the totals refetch.
//
// The reason is a DROPDOWN of the fixed discount-reason list, as in pm-react's Edit appointment
// → Billing tab (`management-discount-dialog.tsx`). The chosen label ships as `reason`.
import { useState } from 'react';
import { Modal, Button, TextField, RadioGroup, Select } from '@scripsteam/scrips-signal-ds';
import { DISCOUNT_REASON_OPTIONS } from '@/billing/lib/discount-reasons';

export type ManagementDiscountType = 'Amt' | 'Pct';

export interface InvoiceManagementDiscountDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-formatted currency code for the Amount affordance, e.g. "AED". */
  currencyCode: string;
  onApply: (v: { type: ManagementDiscountType; amount: number; reason: string }) => void;
  busy?: boolean;
}

const TYPES = [
  { label: 'Amount', value: 'Amt' },
  { label: 'Percentage', value: 'Pct' },
];

export function InvoiceManagementDiscountDialog({
  open,
  onClose,
  currencyCode,
  onApply,
  busy,
}: InvoiceManagementDiscountDialogProps) {
  const [type, setType] = useState<ManagementDiscountType>('Amt');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const numeric = Number(amount);
  // A percentage over 100 (or any non-positive value) is not a discount — Flutter's popup
  // validates before enabling Apply, and the reason is mandatory on its form.
  const amountValid = Number.isFinite(numeric) && numeric > 0 && (type !== 'Pct' || numeric <= 100);
  const canApply = amountValid && reason.trim().length > 0 && !busy;

  function reset() {
    setType('Amt');
    setAmount('');
    setReason('');
  }

  function handleApply() {
    if (!canApply) return;
    onApply({ type, amount: numeric, reason: reason.trim() });
    reset();
    onClose();
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={() => { reset(); onClose(); }} disabled={busy}>
        Cancel
      </Button>
      <Button onClick={handleApply} disabled={!canApply}>
        {busy ? 'Applying…' : 'Apply'}
      </Button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title="Management Discount" footer={footer} size="sm">
      <div className="flex flex-col gap-4">
        <RadioGroup
          name="management-discount-type"
          options={TYPES}
          value={type}
          onChange={(v) => setType(v as ManagementDiscountType)}
        />
        <TextField
          label={type === 'Pct' ? 'Percentage (%)' : `Amount (${currencyCode})`}
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
        <Select
          label="Reason"
          value={reason}
          onChange={(v) => setReason(v ?? '')}
          placeholder="Select a reason"
          options={DISCOUNT_REASON_OPTIONS}
        />
      </div>
    </Modal>
  );
}
